import { DateTime } from 'luxon';

import { Box, Group, Text, rem } from '@optiro/ui';

import { NotificationContent } from './NotificationContent';
import { NotificationIcon } from './NotificationIcon';
import { NotificationType, categoryConfig, getCategory } from './notifications.types';

interface NotificationItemProps {
  n: NotificationType;
  onItemClick: (n: NotificationType) => void;
}

export function NotificationItem({ n, onItemClick }: NotificationItemProps) {
  const isUnread = !n.read_at;

  const category = getCategory(n);
  const config = categoryConfig[category] ?? categoryConfig.alert;

  return (
    <Box
      onClick={() => onItemClick(n)}
      px="sm"
      py="xs"
      style={(theme) => ({
        borderRadius: theme.radius.md,
        cursor: 'pointer',
        backgroundColor: isUnread ? theme.colors.blue[0] : 'transparent',
        transition: 'background-color 120ms ease',
        '&:hover': {
          backgroundColor: theme.colors.gray[1],
        },
        borderLeft: isUnread
          ? `3px solid ${theme.colors[config.color][5]}`
          : '3px solid transparent',
      })}
      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--mantine-color-gray-1)')}
      onMouseLeave={(e) =>
        (e.currentTarget.style.backgroundColor = isUnread
          ? 'var(--mantine-color-blue-0)'
          : 'transparent')
      }
    >
      <Group gap="sm" align="flex-start" wrap="nowrap">
        {/* Icon / Avatar column */}
        <NotificationIcon n={n} category={category} config={config} />

        {/* Content column */}
        <Box style={{ flex: 1, minWidth: 0 }}>
          <NotificationContent n={n} />
          <Text size="xs" c="dimmed" mt={4}>
            {DateTime.fromISO(n.created_at).toRelative({ locale: 'da' })}
          </Text>
        </Box>

        {/* Unread dot */}
        {isUnread && (
          <Box
            style={{
              width: rem(8),
              height: rem(8),
              borderRadius: '50%',
              backgroundColor: `var(--mantine-color-${config.color}-5)`,
              flexShrink: 0,
              marginTop: rem(6),
            }}
          />
        )}
      </Group>
    </Box>
  );
}
